<template>
  <section
    class="OnboardingPage"
    data-theme="dark"
    :data-has-theme="hasTheme"
  >
    <template v-if="isPlayerLoaded">
      <div
        ref="trackRef"
        class="OnboardingPage__track"
      >
        <div class="OnboardingPage__featureList">
          <OnboardingThemeView
            ref="themeRef"
            v-model:player="player"
            data-testid="theme"
            @submit="handleThemeSubmit"
          />
          <OnboardingIntroView
            ref="introRef"
            v-model:player="player"
            data-testid="intro"
          />
          <OnboardingStatusView
            ref="statusRef"
            v-model:player="player"
            data-testid="statuses"
          />
          <OnboardingInviteView
            ref="inviteRef"
            data-testid="invites"
          />
          <OnboardingNotificationView
            ref="notificationRef"
            v-model:player="player"
            data-testid="notifications"
          />
          <OnboardingProfileView
            ref="profileRef"
            v-model:player="player"
            v-bind="{isSaving}"
            data-testid="profile"
            @submit="handleSubmit"
          />
        </div>
      </div>
      <div
        class="OnboardingPage__paginationList"
        :data-is-visible="hasTheme"
      >
        <button
          v-for="view in views"
          :key="view.routeName"
          type="button"
          class="OnboardingPage__paginationItem"
          :aria-current="view.routeName === route.name"
          @click="handlePaginationClick(view.routeName)"
        />
      </div>
    </template>
  </section>
</template>

<script lang="ts" setup>
import { supabaseClient } from "@/lib/database/supabase";
import { RouteName } from "@/lib/router/routes";
import useAuthStore from "@/stores/useAuthStore";
import OnboardingIntroView from "@/views/OnboardingIntroView.vue";
import OnboardingInviteView from "@/views/OnboardingInviteView.vue";
import OnboardingNotificationView from "@/views/OnboardingNotificationView.vue";
import OnboardingProfileView from "@/views/OnboardingProfileView.vue";
import OnboardingStatusView from "@/views/OnboardingStatusView.vue";
import OnboardingThemeView from "@/views/OnboardingThemeView.vue";
import { Theme } from "@shared/lib/constants/themes";
import { TablesInsert } from "@shared/lib/database/database.types";
import pick from "@shared/lib/utils/object/pick";
import { useSeoMeta } from "@unhead/vue";
import { storeToRefs } from "pinia";
import { computed, onBeforeUnmount, ref, useTemplateRef, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

const router = useRouter();
const route = useRoute();

const {
  currentUser,
  currentPlayer,
  isPlayerLoaded,
} = storeToRefs(useAuthStore());

const currentPlayerProfile = computed((): TablesInsert<"players"> | undefined => {
  if (!currentPlayer.value) return;

  return pick(currentPlayer.value, [
    "name",
    "email",
    "theme",
    "position",
    "height",
  ]);
});

const player = ref<TablesInsert<"players">>({
  name: currentUser.value?.user_metadata.name || "",
  email: currentUser.value?.email || "",
  theme: Theme.LosAngelesLakers,
  position: undefined,
  ...currentPlayerProfile.value,
});

const isSaving = ref(false);
const hasTheme = ref(false);

watch(currentPlayerProfile, () => {
  Object.assign(player.value, currentPlayerProfile.value);
});

const trackRef = useTemplateRef<HTMLDivElement>("trackRef");
const themeRef = useTemplateRef<InstanceType<typeof OnboardingThemeView>>("themeRef");
const introRef = useTemplateRef<InstanceType<typeof OnboardingIntroView>>("introRef");
const statusRef = useTemplateRef<InstanceType<typeof OnboardingStatusView>>("statusRef");
const inviteRef = useTemplateRef<InstanceType<typeof OnboardingInviteView>>("inviteRef");
const notificationRef = useTemplateRef<InstanceType<typeof OnboardingNotificationView>>("notificationRef");
const profileRef = useTemplateRef<InstanceType<typeof OnboardingProfileView>>("profileRef");

const viewRefs = [
  themeRef,
  introRef,
  statusRef,
  inviteRef,
  notificationRef,
  profileRef,
];

const views = [
  {
    ref: themeRef,
    title: "Pick your theme",
    routeName: RouteName.onboardingTheme,
  },
  {
    ref: introRef,
    title: "Intro",
    routeName: RouteName.onboardingIntro,
  },
  {
    ref: statusRef,
    title: "Who’s in or out",
    routeName: RouteName.onboardingStatuses,
  },
  {
    ref: inviteRef,
    title: "Invite your players",
    routeName: RouteName.onboardingInvites,
  },
  {
    ref: notificationRef,
    title: "Get notified",
    routeName: RouteName.onboardingNotifications,
  },
  {
    ref: profileRef,
    title: "Update your profile",
    routeName: RouteName.onboardingProfile,
  },
];

const intersectionObserver = ref<IntersectionObserver | null>(null);
const isRouteChangeFromScroll = ref(false);

const currentView = computed(() => {
  return views.find((view) => view.routeName === route.name);
});

useSeoMeta({
  title: () => {
    return currentView.value?.title;
  },
});

onBeforeUnmount(() => {
  intersectionObserver.value?.disconnect();
});

watch(viewRefs, () => {
  intersectionObserver.value?.disconnect();

  trackRef.value?.scrollTo({
    left: currentView.value?.ref.value?.$el.offsetLeft || 0,
    behavior: "instant",
  });

  intersectionObserver.value = new IntersectionObserver(handleIntersection, {
    threshold: 0.5,
  });

  viewRefs.forEach((viewRef) => {
    if (viewRef.value) intersectionObserver.value?.observe(viewRef.value.$el);
  });
}, {
  immediate: true,
});

watch(currentView, () => {
  hasTheme.value = hasTheme.value || currentView.value?.routeName !== RouteName.onboardingTheme;

  if (isRouteChangeFromScroll.value) {
    isRouteChangeFromScroll.value = false;
    return;
  }

  trackRef.value?.scrollTo({
    left: currentView.value?.ref.value?.$el.offsetLeft || 0,
    behavior: "smooth",
  });
}, {
  immediate: true,
});

async function handleIntersection (entries: Array<IntersectionObserverEntry>) {
  const entry = entries.find((entry) => entry.isIntersecting);
  if (!entry) return;

  const index = viewRefs.findIndex((viewRef) => viewRef.value?.$el === entry.target);
  if (index === -1) return;

  if (route.name !== views[index].routeName) {
    isRouteChangeFromScroll.value = true;
    router.push({
      name: views[index].routeName,
      query: route.query,
    });
  }
}

async function handleThemeSubmit () {
  hasTheme.value = true;
  trackRef.value?.scrollTo({
    left: introRef.value?.$el.offsetLeft || 0,
    behavior: "smooth",
  });
}

async function handleSubmit () {
  if (!currentUser.value) return;

  isSaving.value = true;
  const {
    data: _player,
    error,
  } = await supabaseClient
    .from("players")
    .upsert({
      ...player.value,
      id: currentPlayer.value?.id,
      user_id: currentUser.value.id,
    })
    .eq("user_id", currentUser.value.id)
    .select("*, crew_memberships:player_crews(*, crew:crews!inner(*))")
    .maybeSingle();

  if (_player) {
    currentPlayer.value = _player;

    const queryRoute = route.query.redirect
      ? router.resolve(decodeURIComponent(route.query.redirect.toString()))
      : null;

    router.push(queryRoute || {
      name: RouteName.index,
    });
  } else if (error) {
    alert(error.message);
    isSaving.value = false;
  }
}

async function handlePaginationClick (routeName: RouteName) {
  const view = views.find((view) => view.routeName === routeName);
  if (!view) return;

  trackRef.value?.scrollTo({
    left: view.ref.value?.$el.offsetLeft || 0,
    behavior: "smooth",
  });
}
</script>

<style lang="scss" scoped>
.OnboardingPage {
  display: grid;
  grid-template-rows: auto min-content;
  gap: 24px;
  height: 100%;
  padding: 16px 0 calc(24px + var(--safeAreaInsetBottom));

  background-color: var(--themeColorPrimary);
  color: var(--themeColorPrimaryForeground);

  transition: 350ms background-color;
}

.OnboardingPage__track {
  overflow-x: auto;
  scroll-snap-type: x mandatory;

  &::-webkit-scrollbar {
    display: none;
  }

  [data-has-theme="false"] & {
    overflow: hidden;
  }
}

.OnboardingPage__featureList {
  display: grid;
  grid-auto-columns: 100%;
  grid-auto-flow: column;
  gap: 32px;
  height: 100%;
}

.OnboardingPage__paginationList {
  display: flex;
  margin-inline: auto;
  transition: 350ms opacity;

  &[data-is-visible="false"] {
    opacity: 0;
  }
}

.OnboardingPage__paginationItem {
  position: relative;

  display: grid;
  place-items: center;
  width: 24px;
  aspect-ratio: 1;

  &::after {
    position: absolute;
    top: calc(50% - 5px);
    left: calc(50% - 5px);

    width: 10px;
    aspect-ratio: 1;

    border-radius: 50%;
    background-color: var(--themeColorWhite);

    transition: 150ms opacity;

    content: "";
  }

  &[aria-current="false"]::after {
    opacity: 0.2;
  }
}
</style>
